import { useEffect, useState, useRef } from 'react';
import {
  DocumentArrowDownIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  PrinterIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline';
import TableComponent from 'components/common/TableComponent';
import { makeRequest } from 'services/request.service';
import { useAuth } from 'auth/AuthContext';
import ButtonComponent from 'components/common/ButtonComponent';
import StickersModal from 'components/modals/StickersModal';
import ImportStickersModal from 'components/modals/ImportStickersModal';
import ActionCardComponent from 'components/common/ActionCardComponent';
import SectionIntro from 'components/common/SectionIntro';
import { stickerTypes, reusePolicies } from 'constants/stickers';
import { removeEmpty } from 'utils/objectUtils';
import { showAlert } from 'services/alerts.service';
import { useTranslation } from 'react-i18next';

export default function Stickers() {
  const { t } = useTranslation();
  const hasFetched = useRef(false);
  const { user } = useAuth();

  const [state, setState] = useState({
    selectedSticker: null,
    stickers: [],
    containers: [],
    workers: [],
    isOpenStickersModal: false,
    isOpenImportModal: false,
    filter: '',
    refresh: 0,
    searchTerm: '',
    isImporting: false
  });

  const getStickers = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/stickers/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      });

      setState(s => ({ ...s, stickers: response.data }));
    } catch (error) {
      setState(s => ({ ...s, stickers: [] }));
    }
  };

  const getContainers = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: '/management/containers',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      });
      setState(s => ({ ...s, containers: response.data }));
    } catch (error) {
      setState(s => ({ ...s, containers: [] }));
    }
  };

  const getWorkers = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/workers-management/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      });
      setState(s => ({ ...s, workers: response.data }));
    } catch (error) {
      setState(s => ({ ...s, workers: [] }));
    }
  };

  const onSave = async formData => {
    formData = removeEmpty(formData);
    try {
      await makeRequest({
        method: 'POST',
        url: `/management/stickers/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        },
        data: formData,
        alertResult: true
      });

      // refrech stickers to refresh the table
      await getStickers();

      toggleStickersModal();
    } catch (error) {
      setState(s => ({ ...s, selectedSticker: formData }));
      showAlert({
        text: error.response?.data?.message || t('stickers.errorCreatingSticker'),
        icon: 'error'
      });
      toggleStickersModal();
    }
  };

  const handlePrintSticker = async sticker => {
    try {
      const token = localStorage.getItem('secret');
      const response = await makeRequest({
        method: 'GET',
        url: `/management/stickers/${sticker.stickerId}/print`,
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      });
      const blob = response.data;
      // Try to get filename from Content-Disposition header if available
      let filename = `sticker_${sticker.stickerId}.pdf`;
      const disposition = response.headers && response.headers['content-disposition'];
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match && match[1]) filename = match[1];
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to print sticker', error);
      showAlert({
        text: error.response?.data?.message || t('stickers.failedToPrint'),
        icon: 'error'
      });
    }
  };

  const handleDelete = async sticker => {
    try {
      const response = await makeRequest({
        method: 'DELETE',
        url: `/management/stickers/${sticker.stickerId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        },
        alertResult: true
      });

      if (response.status === 200) {
        setState(s => ({
          ...s,
          stickers: s.stickers.filter(item => item.workerId !== sticker.workerId)
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleImportFromModal = async (file, isPacking) => {
    setState(s => ({ ...s, isImporting: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await makeRequest({
        method: 'POST',
        url: `/management/stickers/import/${user.farmId}?isPacking=${isPacking}`,
        data: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 200) {
        showAlert({
          text: `${response.data.message}. ${t('stickers.totalImported')}: ${response.data.total}`,
          icon: 'success'
        });

        // Refresh the stickers list
        await getStickers();

        // Close the modal
        toggleImportModal();
      }
    } catch (error) {
      console.error('Import failed:', error);
      showAlert({
        text: error.response?.data?.message || t('stickers.failedToImport'),
        icon: 'error'
      });
    } finally {
      setState(s => ({ ...s, isImporting: false }));
    }
  };

  const toggleImportModal = () => {
    setState(s => ({ ...s, isOpenImportModal: !s.isOpenImportModal }));
  };

  const toggleStickersModal = selectedSticker => {
    setState(s => ({
      ...s,
      isOpenStickersModal: !state.isOpenStickersModal,
      selectedSticker
    }));
  };

  const filteredStickers = state.stickers.filter(
    data =>
      data.workerName?.toLowerCase().includes(state.searchTerm) ||
      data.containerName?.toLowerCase().includes(state.searchTerm) ||
      data.containerMaterial?.toLowerCase().includes(state.searchTerm) ||
      data.stickerType?.toLowerCase().includes(state.searchTerm)
  );

  const stickersTableData = {
    columns: [
      { key: 'stickerId', label: '#' },
      { key: 'workerName', label: t('stickers.worker') },
      { key: 'containerName', label: t('stickers.container') },
      { key: 'containerMaterial', label: t('stickers.material') },
      { key: 'grossWeight', label: t('stickers.weightPerUnit') },
      { key: 'reusePolicy', label: t('stickers.reuse') },
      { key: 'resetEvery', label: t('stickers.reset') },
      { key: 'resetTime', label: t('stickers.resetTime') },
      { key: 'ranges', label: t('stickers.ranges') },
      { key: 'quantity', label: t('stickers.qty') },
      { key: 'stickerType', label: t('stickers.type') },
      { key: 'secondsToClose', label: t('stickers.closeSeconds') },
      {
        key: 'actions',
        label: t('common.manage'),
        cell: (value, sticker) => (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePrintSticker(sticker)}
              title={t('common.print')}
              className="p-1 rounded transition"
            >
              <PrinterIcon className="w-5 h-5 text-green-600" />
            </button>
            <button
              onClick={() => toggleStickersModal(sticker)}
              title={t('common.edit')}
              className="p-1 rounded transition"
            >
              <PencilSquareIcon className="w-5 h-5 text-blue-500" />
            </button>
            <button
              onClick={() => handleDelete(sticker)}
              title={t('common.delete')}
              className="p-1 rounded transition"
            >
              <TrashIcon className="w-5 h-5 text-red-500" />
            </button>
          </div>
        )
      }
    ],
    rows: filteredStickers
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    getStickers();
    getContainers();
    getWorkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen p-6 text-slate-100">
      <SectionIntro
        kicker={t('pageIntro.managementKicker')}
        title={t('nav.stickers')}
        description={t('pageIntro.stickersDescription')}
      />
      <hr className="workspace-summary-divider my-5 border-t" />
      <ActionCardComponent>
        <input
          type="text"
          placeholder={t('stickers.searchSticker')}
          value={state.searchTerm}
          onChange={e => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className="search-input"
          data-align="left"
        />

        <ButtonComponent onClick={() => toggleStickersModal(null)}>
          <PlusIcon className="w-5 h-5 mr-1" />
          {t('stickers.newSticker')}
        </ButtonComponent>

        <ButtonComponent
          onClick={toggleImportModal}
          disabled={state.isImporting}
          className={state.isImporting ? 'opacity-50 cursor-not-allowed' : ''}
        >
          <DocumentArrowUpIcon className="w-5 h-5" />
          <span>{t('stickers.importPDF')}</span>
        </ButtonComponent>

        <ButtonComponent onClick={() => {}}>
          <DocumentArrowDownIcon className="w-5 h-5" />
          <span>{t('export.excel')}</span>
        </ButtonComponent>
        <ButtonComponent onClick={() => {}}>
          <DocumentArrowDownIcon className="w-5 h-5" />
          <span>{t('export.pdf')}</span>
        </ButtonComponent>
      </ActionCardComponent>{' '}
      {/* stickers table */}
      <TableComponent
        tableData={stickersTableData}
        defaultSortColumn={stickersTableData.columns[0]?.key}
      />
      {/* modal add/edit sticker */}
      <StickersModal
        isOpen={state.isOpenStickersModal}
        onClose={toggleStickersModal}
        onSave={onSave}
        selectedSticker={state.selectedSticker}
        stickerTypes={stickerTypes}
        reusePolicies={reusePolicies}
        containers={state.containers}
        workers={state.workers}
      />
      {/* modal import stickers */}
      <ImportStickersModal
        isOpen={state.isOpenImportModal}
        onClose={toggleImportModal}
        onImport={handleImportFromModal}
        isImporting={state.isImporting}
      />
    </div>
  );
}
